import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Check, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * UpgradePanel — replaces the plain "Your usage" card on the Analyze page.
 *
 * Shows the current plan's quota PLUS a clear Free → Pro benefits comparison
 * so users always understand what's gated. Pro users see a condensed version
 * confirming their entitlements.
 *
 * Phase 8.LP.FREE-POLISH — Added a `variant="horizontal"` layout for use
 * as a centered/wide card below the AI Insights / Key Drivers section.
 * The horizontal variant lays quota + benefits side-by-side and is
 * flatter than the sidebar variant, so it reads as an intentional
 * status card instead of a side-panel afterthought. The default
 * `variant="sidebar"` behaviour is preserved for backwards compatibility.
 */
export default function UpgradePanel({
  usage,
  plan = "free",
  variant = "sidebar",
  className = "",
}) {
  const navigate = useNavigate();
  const isPro = (plan || "free").toLowerCase() !== "free";
  const isHorizontal = variant === "horizontal";

  // ─── Horizontal variant (centered, wide, compact) ─────────────
  if (isHorizontal && !isPro) {
    return (
      <div
        className={`rounded-2xl border border-border/70 bg-card/70 p-5 sm:p-6 ${className}`}
        data-testid="upgrade-panel"
        data-variant="horizontal"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 items-center">
          {/* Left — quota */}
          <div className="md:col-span-4 min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Free plan usage
              </div>
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                style={{
                  color: "#3182CE",
                  background: "rgba(49,130,206,0.10)",
                  border: "1px solid rgba(49,130,206,0.30)",
                }}
              >
                Free
              </span>
            </div>
            {usage?.unlimited ? (
              <div className="mt-1 font-display text-2xl">Unlimited</div>
            ) : (
              <>
                <div
                  className="mt-1 font-display text-2xl tabular"
                  data-testid="upgrade-panel-remaining"
                >
                  {usage?.remaining ?? "\u2013"}
                  <span className="text-sm text-muted-foreground ml-1.5">
                    of {usage?.limit ?? "\u2013"} remaining
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Window:{" "}
                  {usage?.phase === "free_first_30d"
                    ? "first 30 days"
                    : `rolling ${usage?.window_days ?? 30} days`}
                </div>
              </>
            )}
          </div>

          {/* Middle — Free includes */}
          <div className="md:col-span-5 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Free includes
            </div>
            <ul
              className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[13px]"
              data-testid="upgrade-panel-free-list"
            >
              <BenefitRow text="10 AI analyses per period" />
              <BenefitRow text="Basic valuation & fair-value range" />
              <BenefitRow text="Limited AI reasoning (preview)" />
              <BenefitRow text="Watchlist & price charts" />
            </ul>
          </div>

          {/* Right — CTA */}
          <div className="md:col-span-3 flex flex-col gap-2 items-stretch md:items-end">
            <Button
              size="sm"
              onClick={() => navigate("/billing")}
              data-testid="upgrade-panel-cta"
              className="w-full md:w-auto"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Upgrade to Pro
            </Button>
            <button
              type="button"
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors text-center md:text-right"
              onClick={() => navigate("/pricing")}
              data-testid="upgrade-panel-see-plans"
            >
              Compare all plans →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Sidebar variant (legacy) ─────────────────────────────────
  return (
    <div
      className={`rounded-2xl border border-border/70 bg-card/70 p-5 ${className}`}
      data-testid="upgrade-panel"
      data-variant="sidebar"
    >
      {/* Quota row */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {isPro ? "Plan" : "Free plan usage"}
          </div>
          {isPro ? (
            <div className="mt-1 font-display text-lg">
              Unlimited <span className="text-sm text-muted-foreground capitalize">· {plan}</span>
            </div>
          ) : usage?.unlimited ? (
            <div className="mt-1 font-display text-lg">Unlimited</div>
          ) : (
            <>
              <div className="mt-1 font-display text-lg tabular">
                {usage?.remaining ?? "\u2013"} <span className="text-sm text-muted-foreground">of {usage?.limit ?? "\u2013"} remaining</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Window:{" "}
                {usage?.phase === "free_first_30d"
                  ? "first 30 days"
                  : `rolling ${usage?.window_days ?? 30} days`}
              </div>
            </>
          )}
        </div>
        {!isPro && (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
            style={{
              color: "#3182CE",
              background: "rgba(49,130,206,0.10)",
              border: "1px solid rgba(49,130,206,0.30)",
            }}
          >
            Free
          </span>
        )}
      </div>

      {!isPro ? (
        <>
          <div className="mt-4 grid grid-cols-1 gap-3">
            {/* Free includes */}
            <div className="rounded-xl border border-border/70 bg-card/40 p-3" data-testid="upgrade-panel-free-list">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Free includes</div>
              <ul className="mt-2 space-y-1.5 text-[13px]">
                <BenefitRow text="10 AI analyses per period" />
                <BenefitRow text="Basic valuation & fair-value range" />
                <BenefitRow text="Limited AI reasoning (preview)" />
                <BenefitRow text="Watchlist & price charts" />
              </ul>
            </div>

            {/* Upgrade for */}
            <div
              className="rounded-xl border p-3 relative overflow-hidden"
              style={{
                borderColor: "rgba(49,130,206,0.36)",
                background:
                  "linear-gradient(180deg, rgba(49,130,206,0.07), rgba(49,130,206,0.02))",
              }}
              data-testid="upgrade-panel-pro-list"
            >
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#3182CE]" />
                <div className="text-[11px] uppercase tracking-wider" style={{ color: "#3182CE" }}>
                  Upgrade for
                </div>
              </div>
              <ul className="mt-2 space-y-1.5 text-[13px]">
                <BenefitRow text="Full AI breakdowns & scenarios" emphasis />
                <BenefitRow text="Unlimited analyses" emphasis />
                <BenefitRow text="Advanced risk detection" emphasis />
                <BenefitRow text="Institutional-grade insights" emphasis />
              </ul>
            </div>
          </div>
          <Button
            size="sm"
            className="mt-4 w-full"
            onClick={() => navigate("/billing")}
            data-testid="upgrade-panel-cta"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Upgrade to Pro
          </Button>
          <button
            type="button"
            className="mt-2 w-full text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => navigate("/pricing")}
            data-testid="upgrade-panel-see-plans"
          >
            Compare all plans →
          </button>
        </>
      ) : (
        <>
          <div className="mt-4 rounded-xl border border-border/70 bg-card/40 p-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Active entitlements</div>
            <ul className="mt-2 space-y-1.5 text-[13px]">
              <BenefitRow text="Unlimited analyses" emphasis />
              <BenefitRow text="Full AI breakdowns" emphasis />
              <BenefitRow text="Advanced risk detection" emphasis />
              <BenefitRow text="Institutional-grade insights" emphasis />
            </ul>
          </div>
          <Button
            size="sm"
            className="mt-3 w-full"
            variant="outline"
            onClick={() => navigate("/billing")}
            data-testid="upgrade-panel-manage"
          >
            Manage subscription
          </Button>
        </>
      )}
    </div>
  );
}

function BenefitRow({ text, emphasis = false }) {
  return (
    <li className="flex items-start gap-2">
      {emphasis ? (
        <Sparkles className="w-3 h-3 mt-1 shrink-0" style={{ color: "#3182CE" }} />
      ) : (
        <Check className="w-3 h-3 mt-1 shrink-0 text-muted-foreground" />
      )}
      <span className={emphasis ? "text-foreground" : "text-foreground/85"}>{text}</span>
    </li>
  );
}

// Convenience export for when callers also need a tiny inline lock badge
export function ProBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider align-middle"
      style={{
        color: "#3182CE",
        background: "rgba(49,130,206,0.10)",
        border: "1px solid rgba(49,130,206,0.30)",
      }}
    >
      <Lock className="w-2.5 h-2.5" /> Pro
    </span>
  );
}
